import { z } from 'zod';

/**
 * Wire schemas mirroring the backend's Pydantic DTOs.
 *
 * These are the contract boundary. When the backend changes shape, exactly one
 * place needs updating and every consumer is re-typed automatically.
 */

export const experienceLevelSchema = z.enum([
  'student',
  'fresher',
  'junior',
  'mid',
  'senior',
  'lead',
  'career_switcher',
]);
export type ExperienceLevel = z.infer<typeof experienceLevelSchema>;

export const appRoleSchema = z.enum(['user', 'admin']);
export type AppRole = z.infer<typeof appRoleSchema>;

export const accountStatusSchema = z.enum(['active', 'suspended']);
export type AccountStatus = z.infer<typeof accountStatusSchema>;

export const dependencyStatusSchema = z.enum(['ok', 'degraded', 'unavailable', 'not_configured']);

export const healthSchema = z.object({
  status: z.literal('ok'),
  service: z.string(),
  version: z.string(),
  environment: z.string(),
  uptime_seconds: z.number(),
});
export type Health = z.infer<typeof healthSchema>;

export const readinessSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  service: z.string(),
  version: z.string(),
  environment: z.string(),
  dependencies: z.array(
    z.object({
      name: z.string(),
      status: dependencyStatusSchema,
      latency_ms: z.number().nullable(),
      detail: z.string().nullable(),
    }),
  ),
});
export type Readiness = z.infer<typeof readinessSchema>;

export const profileSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string().nullable(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  headline: z.string().nullable(),
  target_role: z.string().nullable(),
  target_companies: z.array(z.string()),
  experience_level: experienceLevelSchema,
  experience_level_label: z.string(),
  years_experience: z.number(),
  timezone: z.string(),
  locale: z.string(),
  role: appRoleSchema,
  account_status: accountStatusSchema,
  is_admin: z.boolean(),
  is_onboarded: z.boolean(),
  deletion_requested: z.boolean(),
  deletion_requested_at: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type Profile = z.infer<typeof profileSchema>;

export const adminMeSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: appRoleSchema,
  is_admin: z.boolean(),
});
export type AdminMe = z.infer<typeof adminMeSchema>;

export const adminOnlineUserSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  path: z.string(),
  interview_id: z.string().nullable(),
  last_seen_at: z.string(),
  seconds_ago: z.number(),
  browser: z.string().nullable(),
  os: z.string().nullable(),
  device_type: z.string().nullable(),
});

export const adminLiveActivityEventSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  source: z.string(),
  amount: z.number(),
  created_at: z.string(),
});

export const adminPresenceSchema = z.object({
  online_count: z.number(),
  threshold_seconds: z.number(),
  online: z.array(adminOnlineUserSchema),
  recent_events: z.array(adminLiveActivityEventSchema),
});
export type AdminPresence = z.infer<typeof adminPresenceSchema>;

export const presenceHeartbeatSchema = z.object({
  user_id: z.string(),
  last_seen_at: z.string(),
  path: z.string(),
  interview_id: z.string().nullable(),
});
export type PresenceHeartbeat = z.infer<typeof presenceHeartbeatSchema>;

export interface PresenceHeartbeatPayload {
  path: string;
  interview_id?: string | null;
}

export const analyticsEventIngestSchema = z.object({
  id: z.string(),
  event_name: z.string(),
  path: z.string(),
  session_id: z.string(),
  created_at: z.string().nullable(),
});
export type AnalyticsEventIngest = z.infer<typeof analyticsEventIngestSchema>;

export interface AnalyticsEventPayload {
  session_id: string;
  event_name: string;
  path: string;
  properties?: Record<string, string | number | boolean>;
}

export const adminAnalyticsDaySchema = z.object({
  day: z.string(),
  page_views: z.number(),
  unique_users: z.number(),
  sessions: z.number(),
  events: z.number(),
});

export const adminAnalyticsSchema = z.object({
  range_days: z.number(),
  period_start: z.string(),
  period_end: z.string(),
  totals: z.object({
    page_views: z.number(),
    sessions: z.number(),
    unique_users: z.number(),
    events: z.number(),
  }),
  by_day: z.array(adminAnalyticsDaySchema),
  top_paths: z.array(z.object({ path: z.string(), views: z.number() })),
  by_event: z.array(z.object({ event_name: z.string(), count: z.number() })),
  note: z.string(),
});
export type AdminAnalytics = z.infer<typeof adminAnalyticsSchema>;

export const adminSystemSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  service: z.string(),
  version: z.string(),
  environment: z.string(),
  uptime_seconds: z.number(),
  dependencies: z.array(
    z.object({
      name: z.string(),
      status: dependencyStatusSchema,
      latency_ms: z.number().nullable(),
      detail: z.string().nullable(),
    }),
  ),
  rate_limits: z.object({
    enabled: z.boolean(),
    backend: z.string(),
    tracked_keys: z.number().nullable(),
    budgets: z.array(z.object({ name: z.string(), rule: z.string() })),
  }),
  note: z.string(),
});
export type AdminSystem = z.infer<typeof adminSystemSchema>;

export const adminAuditLogEntrySchema = z.object({
  id: z.string(),
  actor_user_id: z.string(),
  action: z.string(),
  target_user_id: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  created_at: z.string().nullable(),
});

export const adminAuditLogListSchema = z.object({
  items: z.array(adminAuditLogEntrySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type AdminAuditLogList = z.infer<typeof adminAuditLogListSchema>;

export interface AdminAuditLogQuery {
  limit?: number;
  offset?: number;
}

export const adminUserListItemSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string().nullable(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  target_role: z.string().nullable(),
  target_companies: z.array(z.string()),
  experience_level: experienceLevelSchema,
  role: appRoleSchema,
  account_status: accountStatusSchema,
  is_admin: z.boolean(),
  is_onboarded: z.boolean(),
  email_verified: z.boolean(),
  deletion_requested: z.boolean(),
  deletion_requested_at: z.string().nullable(),
  total_xp: z.number(),
  level: z.number(),
  current_streak: z.number(),
  interviews_taken: z.number(),
  resumes_analyzed: z.number(),
  minutes_practiced: z.number(),
  created_at: z.string().nullable(),
  last_sign_in_at: z.string().nullable(),
});
export type AdminUserListItem = z.infer<typeof adminUserListItemSchema>;

export const adminUserListSchema = z.object({
  items: z.array(adminUserListItemSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type AdminUserList = z.infer<typeof adminUserListSchema>;

export interface AdminUsersQuery {
  q?: string;
  role?: AppRole;
  status?: AccountStatus;
  sort?:
    | 'created_at'
    | 'email'
    | 'full_name'
    | 'role'
    | 'account_status'
    | 'total_xp'
    | 'level'
    | 'interviews_taken'
    | 'current_streak'
    | 'last_sign_in_at'
    | 'minutes_practiced';
  direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export const userStatsSchema = z.object({
  total_xp: z.number(),
  level: z.number(),
  xp_into_current_level: z.number(),
  xp_for_next_level: z.number(),
  level_progress_percent: z.number(),
  current_streak: z.number(),
  longest_streak: z.number(),
  last_activity_on: z.string().nullable(),
  interviews_taken: z.number(),
  problems_solved: z.number(),
  resumes_analyzed: z.number(),
  minutes_practiced: z.number(),
  show_on_leaderboard: z.boolean(),
});
export type UserStats = z.infer<typeof userStatsSchema>;

export const currentUserSchema = z.object({
  profile: profileSchema,
  stats: userStatsSchema,
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const dashboardHeatmapDaySchema = z.object({
  day: z.string(),
  event_count: z.number(),
  xp_earned: z.number(),
  kinds: z.array(z.string()),
});

export const dashboardAchievementSchema = z.object({
  code: z.string(),
  title: z.string(),
  description: z.string(),
  icon: z.string(),
  tier: z.enum(['bronze', 'silver', 'gold', 'platinum']),
  xp_reward: z.number(),
  sort_order: z.number(),
  unlocked: z.boolean(),
  unlocked_at: z.string().nullable(),
});

export const dashboardWeakTopicSchema = z.object({
  topic: z.string(),
  count: z.number(),
});

export const dashboardRecentInterviewSchema = z.object({
  id: z.string(),
  mode: z.string(),
  track: z.string().nullable(),
  difficulty: z.string(),
  status: z.string(),
  overall_score: z.number().nullable(),
  created_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});

export const dashboardLeaderboardEntrySchema = z.object({
  rank: z.number(),
  user_id: z.string(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  total_xp: z.number(),
  level: z.number(),
  current_streak: z.number(),
  interviews_taken: z.number(),
  problems_solved: z.number(),
  is_viewer: z.boolean(),
});
export type DashboardLeaderboardEntry = z.infer<typeof dashboardLeaderboardEntrySchema>;

export const publicLeaderboardSchema = z.object({
  items: z.array(dashboardLeaderboardEntrySchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  viewer_rank: z.number().nullable(),
  viewer_opted_in: z.boolean(),
});
export type PublicLeaderboard = z.infer<typeof publicLeaderboardSchema>;

export interface LeaderboardQuery {
  limit?: number;
  offset?: number;
}

export const dashboardSchema = z.object({
  stats: userStatsSchema,
  heatmap: z.array(dashboardHeatmapDaySchema),
  achievements: z.array(dashboardAchievementSchema),
  unlocked_achievement_count: z.number(),
  weak_topics: z.array(dashboardWeakTopicSchema),
  recent_interviews: z.array(dashboardRecentInterviewSchema),
  leaderboard: z.array(dashboardLeaderboardEntrySchema),
  viewer_rank: z.number().nullable(),
  show_on_leaderboard: z.boolean(),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

/** Payload for `PATCH /me`. Every field is optional by design. */
export interface ProfileUpdate {
  full_name?: string | null;
  headline?: string | null;
  target_role?: string | null;
  target_companies?: string[];
  experience_level?: ExperienceLevel;
  years_experience?: number;
  timezone?: string;
  locale?: string;
  mark_onboarded?: boolean;
}

/** Payload for `PATCH /me/stats`. Only client-owned preferences. */
export interface StatsUpdate {
  show_on_leaderboard?: boolean;
}

export const resumeAnalysisStatusSchema = z.enum(['pending', 'completed', 'failed']);

export const resumeSummarySchema = z.object({
  id: z.string(),
  version: z.number(),
  original_filename: z.string(),
  byte_size: z.number(),
  page_count: z.number(),
  target_role: z.string().nullable(),
  created_at: z.string().nullable(),
  analysis_status: resumeAnalysisStatusSchema.nullable(),
  ats_score: z.number().nullable(),
});
export type ResumeSummary = z.infer<typeof resumeSummarySchema>;

export const resumeListSchema = z.object({
  items: z.array(resumeSummarySchema),
  total: z.number(),
});
export type ResumeList = z.infer<typeof resumeListSchema>;

export const resumeAnalysisSchema = z.object({
  id: z.string(),
  status: resumeAnalysisStatusSchema,
  model: z.string().nullable(),
  ats_score: z.number().nullable(),
  ats_summary: z.string().nullable(),
  ats_breakdown: z.record(z.string(), z.unknown()),
  extracted: z.record(z.string(), z.unknown()),
  grammar_issues: z.array(z.unknown()),
  suggestions: z.array(z.unknown()),
  present_keywords: z.array(z.unknown()),
  missing_keywords: z.array(z.unknown()),
  rewritten_resume: z.string().nullable(),
  error_message: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});
export type ResumeAnalysis = z.infer<typeof resumeAnalysisSchema>;

export const resumeDetailSchema = z.object({
  id: z.string(),
  version: z.number(),
  original_filename: z.string(),
  content_type: z.string(),
  byte_size: z.number(),
  page_count: z.number(),
  target_role: z.string().nullable(),
  extracted_text: z.string(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  analysis: resumeAnalysisSchema.nullable(),
});
export type ResumeDetail = z.infer<typeof resumeDetailSchema>;

export const resumeDownloadSchema = z.object({
  url: z.string(),
  expires_in_seconds: z.number(),
  original_filename: z.string(),
});
export type ResumeDownload = z.infer<typeof resumeDownloadSchema>;

export const interviewModeSchema = z.enum(['technical', 'hr', 'resume', 'company']);
export type InterviewMode = z.infer<typeof interviewModeSchema>;

export const interviewDifficultySchema = z.enum(['easy', 'medium', 'hard']);
export type InterviewDifficulty = z.infer<typeof interviewDifficultySchema>;

export const interviewStatusSchema = z.enum(['in_progress', 'completed', 'abandoned']);

export const interviewCatalogSchema = z.object({
  modes: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      description: z.string(),
    }),
  ),
  difficulties: z.array(z.string()),
  tracks: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      description: z.string(),
      category: z.string(),
    }),
  ),
  hr_topics: z.array(z.string()),
  default_question_count: z.number(),
  max_question_count: z.number(),
  default_duration_minutes: z.number().optional(),
  duration_presets: z
    .array(
      z.object({
        minutes: z.number(),
        label: z.string(),
        description: z.string(),
        question_count: z.number(),
        follow_up_budget: z.number(),
        stages: z.array(z.string()),
      }),
    )
    .optional(),
});
export type InterviewCatalog = z.infer<typeof interviewCatalogSchema>;

export const interviewSummarySchema = z.object({
  id: z.string(),
  mode: interviewModeSchema,
  track: z.string().nullable(),
  difficulty: interviewDifficultySchema,
  status: interviewStatusSchema,
  question_count: z.number(),
  answered_count: z.number().nullable().optional(),
  overall_score: z.number().nullable(),
  company_slug: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
  duration_minutes: z.number().nullable().optional(),
  created_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});
export type InterviewSummary = z.infer<typeof interviewSummarySchema>;

export const interviewListSchema = z.object({
  items: z.array(interviewSummarySchema),
  total: z.number(),
});
export type InterviewList = z.infer<typeof interviewListSchema>;

export const interviewQuestionSchema = z.object({
  id: z.string(),
  position: z.number(),
  prompt: z.string(),
  topic: z.string(),
  round_name: z.string().nullable().optional(),
  round_kind: z.string().nullable().optional(),
  is_follow_up: z.boolean().optional(),
  parent_question_id: z.string().nullable().optional(),
});

export const interviewAnswerSchema = z.object({
  id: z.string(),
  question_id: z.string(),
  transcript: z.string(),
  input_mode: z.enum(['text', 'voice']),
  duration_seconds: z.number().nullable(),
  filler_word_count: z.number().nullable(),
  thinking_time_seconds: z.number().nullable().optional(),
  offset_seconds: z.number().nullable().optional(),
  technical_score: z.number(),
  communication_score: z.number(),
  confidence_score: z.number(),
  grammar_score: z.number(),
  overall_score: z.number(),
  feedback: z.string(),
  ideal_answer: z.string(),
  star_coverage: z.record(z.string(), z.unknown()),
  communication_metrics: z.record(z.string(), z.unknown()).optional(),
  coding_metrics: z.record(z.string(), z.unknown()).optional(),
  created_at: z.string().nullable(),
});

export const interviewEventSchema = z.object({
  id: z.string(),
  sequence: z.number(),
  occurred_at: z.string().nullable(),
  offset_seconds: z.number().nullable(),
  event_type: z.string(),
  speaker: z.enum(['ai', 'candidate', 'system']),
  question_id: z.string().nullable().optional(),
  parent_question_id: z.string().nullable().optional(),
  content: z.string().nullable().optional(),
  speech_transcript: z.string().nullable().optional(),
  audio_duration_seconds: z.number().nullable().optional(),
  thinking_time_seconds: z.number().nullable().optional(),
  evaluation: z.record(z.string(), z.unknown()).optional(),
  communication_metrics: z.record(z.string(), z.unknown()).optional(),
  coding_metrics: z.record(z.string(), z.unknown()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type InterviewEvent = z.infer<typeof interviewEventSchema>;

export const interviewDetailSchema = z.object({
  id: z.string(),
  mode: interviewModeSchema,
  track: z.string().nullable(),
  difficulty: interviewDifficultySchema,
  status: interviewStatusSchema,
  question_count: z.number(),
  resume_id: z.string().nullable(),
  target_role: z.string().nullable(),
  company_slug: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
  model: z.string().nullable(),
  overall_score: z.number().nullable(),
  technical_score: z.number().nullable(),
  communication_score: z.number().nullable(),
  confidence_score: z.number().nullable(),
  grammar_score: z.number().nullable(),
  weak_topics: z.array(z.unknown()),
  strengths: z.array(z.unknown()).optional(),
  improvement_plan: z.string().nullable(),
  duration_minutes: z.number().nullable().optional(),
  follow_up_budget: z.number().optional(),
  follow_ups_used: z.number().optional(),
  stages: z.array(z.unknown()).optional(),
  started_at: z.string().nullable().optional(),
  interviewer_name: z.string().nullable().optional(),
  interviewer_title: z.string().nullable().optional(),
  interviewer_company: z.string().nullable().optional(),
  created_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  turns: z.array(
    z.object({
      question: interviewQuestionSchema,
      answer: interviewAnswerSchema.nullable(),
    }),
  ),
  events: z.array(interviewEventSchema).optional().default([]),
});
export type InterviewDetail = z.infer<typeof interviewDetailSchema>;

export interface InterviewStart {
  mode: InterviewMode;
  difficulty?: InterviewDifficulty;
  question_count?: number;
  duration_minutes?: number;
  track?: string;
  resume_id?: string;
  target_role?: string;
}

export interface CompanyMockStart {
  difficulty?: InterviewDifficulty;
  question_count?: number;
  duration_minutes?: number;
  target_role?: string;
}

export interface AnswerSubmit {
  question_id: string;
  transcript: string;
  input_mode?: 'text' | 'voice';
  duration_seconds?: number;
  filler_word_count?: number;
  thinking_time_seconds?: number;
  offset_seconds?: number;
  communication_metrics?: Record<string, unknown>;
  coding_metrics?: Record<string, unknown>;
}

export interface InterviewEventCreate {
  event_type: 'greeting' | 'wrap_up' | 'stage' | 'thinking';
  speaker?: 'ai' | 'candidate' | 'system';
  content?: string;
  speech_transcript?: string;
  offset_seconds?: number;
  audio_duration_seconds?: number;
  thinking_time_seconds?: number;
  question_id?: string;
  parent_question_id?: string;
  metadata?: Record<string, unknown>;
}

export const codingDifficultySchema = z.enum(['easy', 'medium', 'hard']);
export type CodingDifficulty = z.infer<typeof codingDifficultySchema>;

export const codingSessionStatusSchema = z.enum(['in_progress', 'solved', 'abandoned']);

export const codingProblemSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  difficulty: codingDifficultySchema,
  topics: z.array(z.string()),
  xp_reward: z.number(),
  sort_order: z.number(),
  hint_count: z.number(),
  public_test_count: z.number(),
});
export type CodingProblemSummary = z.infer<typeof codingProblemSummarySchema>;

export const codingProblemListSchema = z.object({
  items: z.array(codingProblemSummarySchema),
  total: z.number(),
});
export type CodingProblemList = z.infer<typeof codingProblemListSchema>;

export const codingProblemDetailSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  difficulty: codingDifficultySchema,
  topics: z.array(z.string()),
  prompt_md: z.string(),
  starter_code: z.string(),
  function_name: z.string(),
  public_tests: z.array(z.record(z.string(), z.unknown())),
  hint_count: z.number(),
  time_complexity: z.string(),
  space_complexity: z.string(),
  xp_reward: z.number(),
});
export type CodingProblemDetail = z.infer<typeof codingProblemDetailSchema>;

export const codingHintSchema = z.object({
  level: z.number(),
  text: z.string(),
});

export const codingSubmissionSchema = z.object({
  id: z.string(),
  kind: z.enum(['run', 'submit']),
  passed: z.number(),
  failed: z.number(),
  total: z.number(),
  runtime_ms: z.number(),
  results: z.array(z.record(z.string(), z.unknown())),
  error_message: z.string().nullable(),
  created_at: z.string().nullable(),
});

export const codingSessionSummarySchema = z.object({
  id: z.string(),
  problem_id: z.string(),
  status: codingSessionStatusSchema,
  hints_revealed: z.number(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  solved_at: z.string().nullable(),
});

export const codingSessionListSchema = z.object({
  items: z.array(codingSessionSummarySchema),
  total: z.number(),
});
export type CodingSessionList = z.infer<typeof codingSessionListSchema>;

export const codingSessionDetailSchema = z.object({
  id: z.string(),
  status: codingSessionStatusSchema,
  code: z.string(),
  hints_revealed: z.number(),
  hint_count: z.number(),
  revealed_hints: z.array(codingHintSchema),
  solution_unlocked: z.boolean(),
  optimal_solution: z.string().nullable(),
  time_complexity: z.string(),
  space_complexity: z.string(),
  complexity_analysis: z.record(z.string(), z.unknown()),
  problem: codingProblemDetailSchema,
  submissions: z.array(codingSubmissionSchema),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  solved_at: z.string().nullable(),
});
export type CodingSessionDetail = z.infer<typeof codingSessionDetailSchema>;

export interface CodingRunRequest {
  code: string;
}

export const mentorSessionStatusSchema = z.enum(['open', 'closed']);

export const mentorCatalogSchema = z.object({
  topics: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
    }),
  ),
});
export type MentorCatalog = z.infer<typeof mentorCatalogSchema>;

export const mentorMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  created_at: z.string().nullable(),
});

export const mentorSessionSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  status: mentorSessionStatusSchema,
  topic: z.string().nullable(),
  resume_id: z.string().nullable(),
  message_count: z.number(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  closed_at: z.string().nullable(),
});

export const mentorSessionListSchema = z.object({
  items: z.array(mentorSessionSummarySchema),
  total: z.number(),
});
export type MentorSessionList = z.infer<typeof mentorSessionListSchema>;

export const mentorSessionDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: mentorSessionStatusSchema,
  topic: z.string().nullable(),
  resume_id: z.string().nullable(),
  model: z.string().nullable(),
  message_count: z.number(),
  messages: z.array(mentorMessageSchema),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  closed_at: z.string().nullable(),
});
export type MentorSessionDetail = z.infer<typeof mentorSessionDetailSchema>;

export interface MentorStart {
  topic?: string;
  resume_id?: string;
  first_message?: string;
}

export interface MentorMessageRequest {
  content: string;
}

export const roadmapStatusSchema = z.enum(['active', 'completed', 'archived']);
export const roadmapMilestoneStatusSchema = z.enum([
  'pending',
  'in_progress',
  'completed',
  'skipped',
]);
export const roadmapMilestoneKindSchema = z.enum([
  'study',
  'interview',
  'coding',
  'resume',
  'review',
]);

export const roadmapMilestoneSchema = z.object({
  id: z.string(),
  week_number: z.number(),
  day_of_week: z.number().nullable(),
  sort_order: z.number(),
  title: z.string(),
  description: z.string(),
  kind: roadmapMilestoneKindSchema,
  topic: z.string().nullable(),
  status: roadmapMilestoneStatusSchema,
  estimated_minutes: z.number(),
  interview_track: z.string().nullable(),
  interview_mode: z.string().nullable(),
  interview_difficulty: z.string().nullable(),
  coding_problem_id: z.string().nullable(),
  coding_slug: z.string().nullable(),
  completed_at: z.string().nullable(),
});

export const roadmapSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  status: roadmapStatusSchema,
  summary: z.string().nullable(),
  target_role: z.string().nullable(),
  target_company: z.string().nullable(),
  experience_level: z.string().nullable(),
  hours_per_week: z.number(),
  week_count: z.number(),
  resume_id: z.string().nullable(),
  completed_milestones: z.number(),
  total_milestones: z.number(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});

export const roadmapListSchema = z.object({
  items: z.array(roadmapSummarySchema),
  total: z.number(),
});
export type RoadmapList = z.infer<typeof roadmapListSchema>;

export const roadmapDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: roadmapStatusSchema,
  summary: z.string().nullable(),
  target_role: z.string().nullable(),
  target_company: z.string().nullable(),
  experience_level: z.string().nullable(),
  hours_per_week: z.number(),
  week_count: z.number(),
  resume_id: z.string().nullable(),
  model: z.string().nullable(),
  generation_context: z.record(z.string(), z.unknown()),
  completed_milestones: z.number(),
  total_milestones: z.number(),
  milestones: z.array(roadmapMilestoneSchema),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});

export const adminUserProfileDetailSchema = adminUserListItemSchema.extend({
  headline: z.string().nullable(),
  years_experience: z.number(),
  timezone: z.string(),
  locale: z.string(),
  longest_streak: z.number(),
  problems_solved: z.number(),
  show_on_leaderboard: z.boolean(),
  onboarded_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  banned_until: z.string().nullable(),
});
export type AdminUserProfileDetail = z.infer<typeof adminUserProfileDetailSchema>;

export const adminXpEventSchema = z.object({
  id: z.string(),
  source: z.string(),
  amount: z.number(),
  reference_id: z.string().nullable(),
  created_at: z.string().nullable(),
});
export type AdminXpEvent = z.infer<typeof adminXpEventSchema>;

export const adminUserDetailSchema = z.object({
  user: adminUserProfileDetailSchema,
  dashboard: dashboardSchema,
  interviews: z.array(interviewSummarySchema),
  resumes: z.array(resumeSummarySchema),
  coding_sessions: z.array(codingSessionSummarySchema),
  mentor_sessions: z.array(mentorSessionSummarySchema),
  roadmaps: z.array(roadmapSummarySchema),
  xp_events: z.array(adminXpEventSchema),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;
export type RoadmapDetail = z.infer<typeof roadmapDetailSchema>;

export interface RoadmapGenerate {
  hours_per_week: number;
  target_company?: string;
  resume_id?: string;
  week_count?: number;
}

export interface RoadmapRegenerate {
  hours_per_week?: number;
  target_company?: string;
  resume_id?: string;
  week_count?: number;
}

export const companySummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  tagline: z.string(),
  region_tags: z.array(z.string()),
  sort_order: z.number(),
  is_target: z.boolean(),
  checklist_total: z.number(),
  checklist_completed: z.number(),
});

export const companyListSchema = z.object({
  items: z.array(companySummarySchema),
  total: z.number(),
});
export type CompanyList = z.infer<typeof companyListSchema>;

export const companyDetailSchema = z.object({
  slug: z.string(),
  name: z.string(),
  tagline: z.string(),
  overview: z.string(),
  region_tags: z.array(z.string()),
  rounds: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
      typical_duration: z.string(),
      focus_areas: z.array(z.string()),
    }),
  ),
  topics: z.array(
    z.object({
      topic: z.string(),
      weight_percent: z.number(),
      notes: z.string(),
    }),
  ),
  faqs: z.array(
    z.object({
      question: z.string(),
      answer: z.string(),
      category: z.string(),
    }),
  ),
  experiences: z.array(
    z.object({
      role: z.string(),
      outcome: z.string(),
      summary: z.string(),
    }),
  ),
  checklist: z.array(
    z.object({
      code: z.string(),
      title: z.string(),
      description: z.string(),
      category: z.string(),
      estimated_minutes: z.number(),
      completed: z.boolean(),
    }),
  ),
  sort_order: z.number(),
  is_target: z.boolean(),
});
export type CompanyDetail = z.infer<typeof companyDetailSchema>;
